import {
  afterRenderEffect,
  AfterViewInit,
  booleanAttribute,
  Directive,
  effect,
  ElementRef,
  input,
  NgZone,
  OnDestroy,
  untracked,
} from '@angular/core';
import { debounceTime, distinctUntilChanged, ReplaySubject } from 'rxjs';
import { outputFromObservable, toObservable } from '@angular/core/rxjs-interop';

@Directive({
  selector: '[softObserveElementSize]',
  standalone: true,
})
export class ObserveElementSizeDirective implements AfterViewInit, OnDestroy {
  isObserving = false;

  private observer?: MutationObserver;
  private resizeObserver?: ResizeObserver;

  readonly observe = input(true, {
    alias: 'softObserveElementSize',
    transform: booleanAttribute,
  });

  readonly #domRect$ = new ReplaySubject<DOMRect>(1);

  readonly contentRect$ = this.#domRect$.pipe(
    distinctUntilChanged((curr, prev) => {
      return curr.height === prev.height && curr.width === prev.width;
    })
  );

  readonly sizeChange = outputFromObservable<DOMRect>(
    this.contentRect$.pipe(debounceTime(100))
  );

  readonly sizeChangeNoDebounce = outputFromObservable<DOMRect>(this.contentRect$);

  constructor(private elementRef: ElementRef) {
    const element = this.elementRef.nativeElement;

    if (!element) return;

    this.observer = new MutationObserver(() => {
      const elementRect = element.getBoundingClientRect();
      this.#domRect$.next(elementRect);
    });

    this.resizeObserver = new ResizeObserver(() => {
      const elementRect = element.getBoundingClientRect();
      this.#domRect$.next(elementRect);
    });

    afterRenderEffect({
      mixedReadWrite: () => {
        if (this.observe()) {
          untracked(() => this.startObservation(element));
        } else {
          untracked(() => this.stopObservation());
        }
      },
    });
  }

  ngAfterViewInit() {
    const element = this.elementRef.nativeElement;
    if (!element) return;
    this.#domRect$.next(element.getBoundingClientRect());
  }

  ngOnDestroy() {
    this.stopObservation();
  }

  startObservation(element: HTMLElement) {
    if (!this.resizeObserver || !this.observer) return;

    if (this.resizeObserver) {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
    }

    this.isObserving = true;

    this.resizeObserver.observe(element);

    this.observer.observe(element, {
      subtree: true,
      childList: true,
      characterData: false,
      attributeOldValue: false,
      characterDataOldValue: false,
      attributes: true,
      attributeFilter: ['style'],
    });
  }

  stopObservation() {
    this.isObserving = false;
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
  }
}
